import React, { useState } from 'react';
import {
  Search, Filter, Download, ChevronLeft, ChevronRight,
  Eye, Package, Truck, CheckCircle, Clock, XCircle,
  Calendar, User, ShoppingBag, DollarSign, MapPin,
  Phone, Mail, RefreshCw, MoreVertical, Printer,
  AlertCircle, TrendingUp, TrendingDown, Star
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PartnerOrders = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState('month');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const itemsPerPage = 5;

  // Static Mock Data
  const orders = [
    {
      id: 'ORD-7829',
      customer: { name: 'Rahul Sharma', avatar: 'R', email: 'rahul@example.com', phone: '+91 98765 43210' },
      amount: 2450,
      status: 'Delivered',
      payment: 'Paid',
      date: 'Oct 24, 2024',
      time: '10:30 AM',
      items: [
        { name: 'Monstera Deliciosa', quantity: 1, price: 1450, image: '🪴' },
        { name: 'Ceramic Pot', quantity: 1, price: 1000, image: '🏺' }
      ],
      shipping: { address: '123 Green Park Avenue, New Delhi - 110001', method: 'Standard Delivery' },
      tracking: 'Delivered on Oct 26, 2024'
    },
    {
      id: 'ORD-7830',
      customer: { name: 'Priya Patel', avatar: 'P', email: 'priya@example.com', phone: '+91 98765 43211' },
      amount: 1200,
      status: 'Delivered',
      payment: 'Paid',
      date: 'Oct 25, 2024',
      time: '02:15 PM',
      items: [
        { name: 'Snake Plant', quantity: 2, price: 600, image: '🌿' }
      ],
      shipping: { address: '45 Lake View Road, Mumbai - 400001', method: 'Express Delivery' },
      tracking: 'Delivered on Oct 27, 2024'
    },
    {
      id: 'ORD-7831',
      customer: { name: 'Amit Singh', avatar: 'A', email: 'amit@example.com', phone: '+91 98765 43212' },
      amount: 4890,
      status: 'Processing',
      payment: 'Pending',
      date: 'Oct 25, 2024',
      time: '04:45 PM',
      items: [
        { name: 'Fiddle Leaf Fig', quantity: 1, price: 3890, image: '🌳' },
        { name: 'Plant Stand', quantity: 1, price: 1000, image: '🪑' }
      ],
      shipping: { address: '789 Royal Enclave, Bengaluru - 560001', method: 'Standard Delivery' },
      tracking: 'Processing at warehouse'
    },
    {
      id: 'ORD-7832',
      customer: { name: 'Sneha Gupta', avatar: 'S', email: 'sneha@example.com', phone: '+91 98765 43213' },
      amount: 850,
      status: 'Shipped',
      payment: 'Paid',
      date: 'Oct 26, 2024',
      time: '09:20 AM',
      items: [
        { name: 'Aloe Vera', quantity: 1, price: 350, image: '🌵' },
        { name: 'Fertilizer', quantity: 2, price: 250, image: '🧴' }
      ],
      shipping: { address: '12 Sunrise Apartments, Pune - 411001', method: 'Express Delivery' },
      tracking: 'Shipped - Expected delivery Oct 30, 2024'
    },
    {
      id: 'ORD-7833',
      customer: { name: 'Vikram Verma', avatar: 'V', email: 'vikram@example.com', phone: '+91 98765 43214' },
      amount: 3100,
      status: 'Cancelled',
      payment: 'Refunded',
      date: 'Oct 26, 2024',
      time: '06:05 PM',
      items: [
        { name: 'Peace Lily', quantity: 1, price: 3100, image: '🌸' }
      ],
      shipping: { address: '56 Garden Colony, Chennai - 600001', method: 'Standard Delivery' },
      tracking: 'Order cancelled by customer'
    },
  ];

  const stats = {
    total: orders.length,
    delivered: orders.filter(o => o.status === 'Delivered').length,
    processing: orders.filter(o => o.status === 'Processing').length,
    shipped: orders.filter(o => o.status === 'Shipped').length,
    cancelled: orders.filter(o => o.status === 'Cancelled').length,
    totalRevenue: orders.reduce((sum, o) => sum + o.amount, 0),
    avgOrderValue: Math.round(orders.reduce((sum, o) => sum + o.amount, 0) / orders.length)
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Delivered': return { label: 'Delivered', icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'Processing': return { label: 'Processing', icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      case 'Shipped': return { label: 'Shipped', icon: Truck, color: '#3b82f6', bg: '#dbeafe' };
      case 'Cancelled': return { label: 'Cancelled', icon: XCircle, color: '#ef4444', bg: '#fee2e2' };
      default: return { label: status, icon: Package, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const getPaymentConfig = (payment) => {
    switch (payment) {
      case 'Paid': return { label: 'Paid', color: '#10b981', bg: '#d1fae5' };
      case 'Pending': return { label: 'Pending', color: '#f59e0b', bg: '#fed7aa' };
      case 'Refunded': return { label: 'Refunded', color: '#ef4444', bg: '#fee2e2' };
      default: return { label: payment, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          order.customer.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={18} color={color} />
      </div>
      <div className="stat-info">
        <h3>{value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  return (
    <div className="partner-orders-modern">
      {/* Header */}
      <div className="orders-header">
        <div>
          <h1 className="page-title">Orders Overview</h1>
          <p className="page-subtitle">Track and monitor all platform orders</p>
        </div>
        <div className="header-actions">
          <div className="date-range">
            <Calendar size={16} />
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last Quarter</option>
            </select>
          </div>
          <button className="btn-export">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Orders" value={stats.total} icon={ShoppingBag} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Delivered" value={stats.delivered} icon={CheckCircle} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Processing" value={stats.processing} icon={Clock} color="#f59e0b" bgColor="#fed7aa" />
        <StatCard title="Shipped" value={stats.shipped} icon={Truck} color="#8b5cf6" bgColor="#e9d5ff" />
        <StatCard title="Total Revenue" value={`₹${stats.totalRevenue.toLocaleString()}`} icon={DollarSign} color="#ef4444" bgColor="#fee2e2" />
        <StatCard title="Avg Order Value" value={`₹${stats.avgOrderValue.toLocaleString()}`} icon={TrendingUp} color="#06b6d4" bgColor="#cffafe" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by Order ID or Customer..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="Delivered">Delivered</option>
            <option value="Shipped">Shipped</option>
            <option value="Processing">Processing</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Items</th>
                <th>Amount</th>
                <th>Payment</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map((order) => {
                const statusConfig = getStatusConfig(order.status);
                const StatusIcon = statusConfig.icon;
                const paymentConfig = getPaymentConfig(order.payment);
                
                return (
                  <tr key={order.id}>
                    <td className="order-id">{order.id}</td>
                    <td className="customer-cell">
                      <div className="customer-avatar">
                        <span>{order.customer.avatar}</span>
                      </div>
                      <div>
                        <div className="customer-name">{order.customer.name}</div>
                        <div className="customer-contact">{order.customer.phone}</div>
                      </div>
                    </td>
                    <td className="date-cell">
                      <div className="date-info">
                        <div>{order.date}</div>
                        <div className="time">{order.time}</div>
                      </div>
                    </td>
                    <td className="items-cell">{order.items.length} items</td>
                    <td className="amount-cell">₹{order.amount.toLocaleString()}</td>
                    <td>
                      <span className="payment-badge" style={{ backgroundColor: paymentConfig.bg, color: paymentConfig.color }}>
                        {paymentConfig.label}
                      </span>
                    </td>
                    <td>
                      <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="action-buttons">
                        <button className="action-btn view" onClick={() => handleViewDetails(order)} title="View Details">
                          <Eye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <div className="pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
            </div>
            <div className="pagination-buttons">
              <button 
                className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Order Details</h3>
                <p className="modal-id">{selectedOrder.id}</p>
              </div>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {/* Customer Info */}
              <div className="detail-section">
                <h4>Customer Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <User size={14} />
                    <span>{selectedOrder.customer.name}</span>
                  </div>
                  <div className="info-item">
                    <Mail size={14} />
                    <span>{selectedOrder.customer.email}</span>
                  </div>
                  <div className="info-item">
                    <Phone size={14} />
                    <span>{selectedOrder.customer.phone}</span>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="detail-section">
                <h4>Order Items</h4>
                <div className="items-list">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="item-row">
                      <div className="item-icon">{item.image}</div>
                      <div className="item-details">
                        <div className="item-name">{item.name}</div>
                        <div className="item-meta">Qty: {item.quantity}</div>
                      </div>
                      <div className="item-price">₹{item.price}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Info */}
              <div className="detail-section">
                <h4>Shipping Information</h4>
                <div className="info-item">
                  <MapPin size={14} />
                  <span>{selectedOrder.shipping.address}</span>
                </div>
                <div className="info-item">
                  <Truck size={14} />
                  <span>{selectedOrder.shipping.method}</span>
                </div>
                <div className="info-item">
                  <Clock size={14} />
                  <span>{selectedOrder.tracking}</span>
                </div>
              </div>

              {/* Order Summary */}
              <div className="detail-section">
                <h4>Order Summary</h4>
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>₹{selectedOrder.amount}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>Free</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>₹{selectedOrder.amount}</span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetailsModal(false)}>Close</button>
              <button className="btn-primary">Print Invoice</button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .partner-orders-modern {
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }

        /* Header */
        .orders-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 4px;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .date-range {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
        }

        .date-range select {
          border: none;
          background: transparent;
          font-size: 13px;
          cursor: pointer;
        }

        .btn-export {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          cursor: pointer;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 16px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: white;
          border-radius: 16px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          border: 1px solid #e8ecef;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }

        .stat-icon {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
        }

        .stat-info h3 {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .stat-info span {
          font-size: 11px;
          color: #64748b;
        }

        /* Filters */
        .filters-bar {
          background: white;
          border-radius: 16px;
          padding: 12px 20px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          border: 1px solid #e8ecef;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 320px;
        }

        .search-box svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-box input {
          width: 100%;
          padding: 8px 16px 8px 38px;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
        }

        .filter-group {
          display: flex;
          gap: 12px;
        }

        .filter-select {
          padding: 6px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          background: white;
        }

        /* Table */
        .table-container {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid #e8ecef;
        }

        .orders-table {
          width: 100%;
          border-collapse: collapse;
        }

        .orders-table th {
          text-align: left;
          padding: 16px 20px;
          background: #f8fafc;
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          border-bottom: 1px solid #e8ecef;
        }

        .orders-table td {
          padding: 16px 20px;
          border-bottom: 1px solid #e8ecef;
          font-size: 13px;
        }

        .orders-table tr:hover {
          background: #f8fafc;
        }

        .order-id {
          font-weight: 600;
          font-family: monospace;
          color: #10b981;
        }

        .customer-cell {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .customer-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 14px;
        }

        .customer-name {
          font-weight: 600;
          color: #1e293b;
        }

        .customer-contact {
          font-size: 11px;
          color: #94a3b8;
        }

        .date-info {
          font-size: 12px;
        }

        .time {
          font-size: 10px;
          color: #94a3b8;
        }

        .amount-cell {
          font-weight: 700;
          color: #10b981;
        }

        .payment-badge, .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
        }

        .text-right {
          text-align: right;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .action-btn {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-btn.view:hover {
          background: #dbeafe;
          border-color: #3b82f6;
          color: #3b82f6;
        }

        /* Pagination */
        .pagination-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-top: 1px solid #e8ecef;
          flex-wrap: wrap;
          gap: 16px;
        }

        .pagination-info {
          font-size: 13px;
          color: #64748b;
        }

        .pagination-buttons {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .page-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
        }

        .page-btn.disabled, .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-number {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
        }

        .page-number.active {
          background: #10b981;
          border-color: #10b981;
          color: white;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }

        .modal-container {
          background: white;
          border-radius: 24px;
          width: 90%;
          max-width: 550px;
          max-height: 85vh;
          overflow-y: auto;
          animation: modalSlideIn 0.3s ease;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e8ecef;
        }

        .modal-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .modal-id {
          font-size: 12px;
          color: #94a3b8;
          margin-top: 4px;
        }

        .modal-close {
          width: 32px;
          height: 32px;
          background: #f1f5f9;
          border: none;
          border-radius: 8px;
          font-size: 20px;
          cursor: pointer;
        }

        .modal-body {
          padding: 24px;
        }

        .detail-section {
          margin-bottom: 24px;
        }

        .detail-section h4 {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e8ecef;
        }

        .info-grid {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .info-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          color: #475569;
        }

        .items-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .item-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .item-icon {
          font-size: 28px;
        }

        .item-details {
          flex: 1;
        }

        .item-name {
          font-weight: 600;
          color: #1e293b;
        }

        .item-meta {
          font-size: 11px;
          color: #64748b;
        }

        .item-price {
          font-weight: 600;
          color: #10b981;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          font-size: 13px;
        }

        .summary-row.total {
          border-top: 1px solid #e8ecef;
          margin-top: 8px;
          padding-top: 12px;
          font-weight: 700;
          font-size: 16px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e8ecef;
        }

        .btn-primary, .btn-secondary {
          padding: 8px 20px;
          border-radius: 40px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
        }

        .btn-primary {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          color: white;
        }

        .btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #64748b;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .stats-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 768px) {
          .partner-orders-modern {
            padding: 16px;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .filters-bar {
            flex-direction: column;
            align-items: stretch;
          }
          .search-box {
            max-width: none;
          }
          .orders-table {
            font-size: 12px;
          }
          .pagination-wrapper {
            flex-direction: column;
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
};

export default PartnerOrders;
